import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { WebsocketContext } from "../../contexts/WebsocketAuthContext";
import { useGetOrderbook } from "../api/orderbook/useGetOrderbook";
import { useMarketDecimals } from "../useMarketDecimals";
import type { MarketDecimals } from "../../utils/rawConversion";
import { GetOrderbook200Response, OrderTypeResponse } from "../../codegen-api";
import {
  convertOrderbookPrice,
  convertOrderbookQuantity,
} from "../../utils/rawConversion";
import {
  clearScheduledRevalidations,
  scheduleRevalidations,
} from "../../utils/revalidation";
import { shouldRunRealtimeRevalidation } from "../../utils/realtimeRevalidation";
import { BoundedKeySet } from "../../utils/boundedKeySet";
import { forEachFrame, readFrame } from "./parseFrame";
import { Overlay } from "./useOrderbookWSS.types";
import {
  getOrderbookChannel,
  getTradesChannel,
  normalizeOrderbookFormat,
  isIndexerOrderEvent,
  newOverlay,
  priceKey,
  ORDERBOOK_EVENT_CACHE_LIMIT,
  buildOrderbookEventKey,
  composeOrderbook,
  getTradeFrameId,
  isNotOlderBook,
  OrderbookStamp,
  stampOrderbook,
} from "./useOrderbookWSS.helpers";

const useOrderbookWSS = (
  instrument?: string,
  orderType: OrderTypeResponse = OrderTypeResponse.Market
) => {
  const { triggerSubscribe, triggerUnsubscribe, registerRevalidate, lastMessages } =
    useContext(WebsocketContext);
  const { data: orderbookData, mutate: mutateOrderbook } = useGetOrderbook(instrument);

  // The market's scale. `undefined` until /markets resolves, which routinely
  // happens AFTER the first /orderbook response: this memo used to convert
  // with a `{18, 6}` guess and, because the decimals were not a dependency,
  // never re-converted once the real 18/18 arrived.
  const decimals = useMarketDecimals(instrument);

  const normalizedSnapshot = useMemo(
    () => normalizeOrderbookFormat(orderbookData, decimals),
    [orderbookData, decimals]
  );

  const [obData, setObData] = useState<GetOrderbook200Response | undefined>(
    normalizedSnapshot
  );

  // Per-instrument overlay reflecting WS-observed level changes since the last
  // snapshot. Reset whenever we get a fresh snapshot or change instruments.
  const overlayRef = useRef<Overlay>(newOverlay());
  // The snapshot the overlay applies to: whichever of the REST and websocket
  // snapshots describes the newer book (FE-10).
  const baseRef = useRef<{ book?: GetOrderbook200Response; stamp?: OrderbookStamp }>({});
  const processedOrderbookEventKeysRef = useRef(
    new BoundedKeySet(ORDERBOOK_EVENT_CACHE_LIMIT)
  );
  const seenTradeIdsRef = useRef(new BoundedKeySet(ORDERBOOK_EVENT_CACHE_LIMIT));
  const revalidationTimersRef = useRef<number[]>([]);
  const lastRevalidationRef = useRef(0);
  // Read at call time, so a new `mutate` identity never re-runs the effects below.
  const mutateOrderbookRef = useRef(mutateOrderbook);
  mutateOrderbookRef.current = mutateOrderbook;
  // The message batch last folded in. A batch is applied once: re-running the
  // effect over it (any dependency change) would re-apply its snapshot.
  const processedBatchRef = useRef<{
    messages: unknown;
    instrument?: string;
    orderType?: OrderTypeResponse;
    decimals?: MarketDecimals;
  }>();

  useEffect(() => {
    overlayRef.current = newOverlay();
    baseRef.current = {};
    processedOrderbookEventKeysRef.current.clear();
    seenTradeIdsRef.current.clear();
  }, [instrument]);

  // Subscribe to both orderbook and trades channels for this instrument
  useEffect(() => {
    if (!instrument || orderType === OrderTypeResponse.Limit) return undefined;
    const channels = [getOrderbookChannel(instrument), getTradesChannel(instrument)];
    triggerSubscribe("subscribe", channels);
    // A failed server snapshot or dropped book/trade frames: refetch the REST book.
    const unregister = registerRevalidate?.(channels, () => {
      void mutateOrderbookRef.current();
    });
    return () => {
      unregister?.();
      triggerUnsubscribe(channels);
    };
  }, [instrument, orderType, registerRevalidate, triggerSubscribe, triggerUnsubscribe]);

  const revalidateOrderbookSoon = useCallback(() => {
    if (!instrument) {
      return;
    }
    if (!shouldRunRealtimeRevalidation(`orderbook:${instrument}`, 10000)) {
      return;
    }
    scheduleRevalidations(
      () => {
        void mutateOrderbookRef.current();
      },
      revalidationTimersRef,
      lastRevalidationRef,
      {
        delays: [3000],
        throttleMs: 10000,
        includeImmediate: false,
      }
    );
  }, [instrument]);

  useEffect(
    () => () => clearScheduledRevalidations(revalidationTimersRef),
    []
  );

  /** Render `base` plus the overlay; true when the result is still crossed. */
  const showBook = useCallback(
    (base: GetOrderbook200Response | undefined) => {
      const { book, crossed, overlayDiscarded } = composeOrderbook(
        base,
        overlayRef.current,
        instrument
      );
      if (overlayDiscarded) overlayRef.current = newOverlay();
      setObData(book);
      return crossed;
    },
    [instrument]
  );

  // When the REST snapshot changes, reset the overlay and re-render.
  //
  // A snapshot is authoritative. The overlay only exists to smooth the gap
  // between snapshots by folding in live WS add/cancel events. It MUST be reset
  // on every fresh snapshot: a WS "add" event bumps a level additively (see below), and the next
  // snapshot already contains that same order — so if the overlay persists across snapshots,
  // the order is counted twice and the book displays ~2x the real liquidity. That phantom depth is
  // what let a $100 order look fillable against a book that only really held ~$65, then revert
  // on-chain with InsufficientLiquidity(). The previous key-gated reset (timestamp + level counts)
  // almost never fired while the maker's book shape was stable, so the double-count was permanent.
  //
  // A REST answer older than the websocket snapshot already shown is ignored
  // (FE-10): it used to overwrite the newer book.
  useEffect(() => {
    if (!instrument || !normalizedSnapshot) return;
    const stamp = stampOrderbook(normalizedSnapshot, Date.now());
    if (!isNotOlderBook(stamp, baseRef.current.stamp)) return;
    baseRef.current = { book: normalizedSnapshot, stamp };
    overlayRef.current = newOverlay();
    showBook(normalizedSnapshot);
  }, [normalizedSnapshot, instrument, showBook]);

  // Fold WS messages into the overlay
  useEffect(() => {
    if (!lastMessages || !instrument || orderType !== OrderTypeResponse.Market)
      return;
    // No scale, no conversion. Returning before the batch is marked processed
    // is deliberate: the same frames are folded again once /markets lands,
    // instead of being dropped or folded at a guessed 10^12 error.
    if (!decimals) return;
    const processed = processedBatchRef.current;
    if (
      processed &&
      processed.messages === lastMessages &&
      processed.instrument === instrument &&
      processed.orderType === orderType &&
      processed.decimals === decimals
    ) {
      return;
    }
    processedBatchRef.current = { messages: lastMessages, instrument, orderType, decimals };

    const obChannel = getOrderbookChannel(instrument);
    const obChannelLower = obChannel.toLowerCase();
    const trChannel = getTradesChannel(instrument);
    const trChannelLower = trChannel.toLowerCase();
    const { baseDecimals, quoteDecimals } = decimals;
    const overlay = overlayRef.current;
    const rememberOrderbookEvent = (eventKey: string) =>
      processedOrderbookEventKeysRef.current.add(eventKey);

    let mutated = false;
    let snapshotApplied: GetOrderbook200Response | undefined;
    let snapshotStamp: OrderbookStamp | undefined;
    let sawNewTrade = false;

    forEachFrame(lastMessages, "useOrderbookWSS", (lastMessage) => {
      const parsed = readFrame(lastMessage);
      if (!parsed) return;
      const { data, channel } = parsed;
      if (typeof channel !== "string" || !data) return;

      const isOb = channel === obChannel || channel.toLowerCase() === obChannelLower;
      const isTrade = channel === trChannel || channel.toLowerCase() === trChannelLower;
      if (!isOb && !isTrade) return;

      // Orderbook snapshot — replace base
      if (isOb && data.type === "snapshot" && (data.bids || data.asks)) {
        const normalized = normalizeOrderbookFormat(data, decimals);
        snapshotApplied = {
          ...normalized,
          instrument_name: instrument,
        } as GetOrderbook200Response;
        snapshotStamp = stampOrderbook(data, Date.now());
        return;
      }

      // Trade events from the backend are periodic recent-trade replays, not
      // guaranteed one-shot deltas. Do not subtract them from the book or old
      // trades will be applied repeatedly. A trade id not seen before is the
      // only prompt to refresh the authoritative snapshot (FE-10): replays used
      // to trigger a refetch about every 10 s per market.
      if (isTrade) {
        const tradeId = getTradeFrameId(data);
        if (tradeId && seenTradeIdsRef.current.add(tradeId)) {
          sawNewTrade = true;
        }
        return;
      }

      // Indexer order event — only act on partial fills / cancels of OPEN/orders
      // we still consider "live". For OPEN add events we additively bump levels.
      if (isOb && isIndexerOrderEvent(data)) {
        const order = data.order;
        const action = data.action;
        const status = order.status;
        const side = order.side; // "Buy" → bids, "Sell" → asks
        const sideKey = side === "Buy" ? "bids" : "asks";
        const priceDisplay = convertOrderbookPrice(order.price, baseDecimals, quoteDecimals);
        const k = priceKey(priceDisplay);

        // New limit order resting on book
        if (action === "add" && status === "OPEN") {
          if (!rememberOrderbookEvent(buildOrderbookEventKey(channel, data))) {
            return;
          }

          const qty = Number(
            convertOrderbookQuantity(
              (BigInt(order.quantity || "0") - BigInt(order.filled || "0")).toString(),
              baseDecimals
            )
          );
          if (qty > 0) {
            overlay[sideKey].set(k, (overlay[sideKey].get(k) || 0) + qty);
            mutated = true;
          }
          return;
        }

        // Order cancelled while still resting — remove its remaining qty
        if (action === "cancel") {
          if (!rememberOrderbookEvent(buildOrderbookEventKey(channel, data))) {
            return;
          }

          const remaining = Number(
            convertOrderbookQuantity(
              (BigInt(order.quantity || "0") - BigInt(order.filled || "0")).toString(),
              baseDecimals
            )
          );
          if (remaining > 0) {
            overlay[sideKey].set(k, (overlay[sideKey].get(k) || 0) - remaining);
            mutated = true;
          }
          return;
        }

        // For "match" / "update" the per-trade decrement is handled via the
        // trades channel; we ignore here to avoid double-counting.
      }
    });

    let crossed = false;
    if (
      snapshotApplied &&
      snapshotStamp &&
      isNotOlderBook(snapshotStamp, baseRef.current.stamp)
    ) {
      baseRef.current = { book: snapshotApplied, stamp: snapshotStamp };
      overlayRef.current = newOverlay();
      crossed = showBook(snapshotApplied);
    } else if (mutated) {
      crossed = showBook(baseRef.current.book);
    }
    if (sawNewTrade || crossed) {
      revalidateOrderbookSoon();
    }
    // `normalizedSnapshot` is deliberately NOT a dependency: re-running this
    // effect over an old batch would re-apply that batch's websocket snapshot
    // on top of a newer REST one.
  }, [decimals, instrument, lastMessages, orderType, revalidateOrderbookSoon, showBook]);

  return {
    orderbook: obData,
  };
};

export default useOrderbookWSS;
